import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

interface DaySchedule {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
  breakStart?: string;
  breakEnd?: string;
  hasBreak: boolean;
}

interface OpeningHoursProps {
  businessId: string;
  initialHours?: Record<string, DaySchedule>;
}

const DEFAULT_HOURS: Record<string, DaySchedule> = {
  Lunes:     { isOpen: true,  openTime: '09:00', closeTime: '20:00', hasBreak: false },
  Martes:    { isOpen: true,  openTime: '09:00', closeTime: '20:00', hasBreak: false },
  Miércoles: { isOpen: true,  openTime: '09:00', closeTime: '20:00', hasBreak: false },
  Jueves:    { isOpen: true,  openTime: '09:00', closeTime: '20:00', hasBreak: false },
  Viernes:   { isOpen: true,  openTime: '09:00', closeTime: '20:00', hasBreak: false },
  Sábado:    { isOpen: true,  openTime: '09:00', closeTime: '14:00', hasBreak: false },
  Domingo:   { isOpen: false, openTime: '10:00', closeTime: '14:00', hasBreak: false },
};

function calcHours(open: string, close: string, breakStart?: string, breakEnd?: string) {
  const [oh, om] = open.split(':').map(Number);
  const [ch, cm] = close.split(':').map(Number);
  let mins = (ch * 60 + cm) - (oh * 60 + om);
  if (breakStart && breakEnd) {
    const [bsh, bsm] = breakStart.split(':').map(Number);
    const [beh, bem] = breakEnd.split(':').map(Number);
    mins -= (beh * 60 + bem) - (bsh * 60 + bsm);
  }
  if (mins <= 0) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h${m > 0 ? ` ${m}min` : ''}`;
}

export function OpeningHours({ businessId, initialHours }: OpeningHoursProps) {
  const [hours, setHours] = useState<Record<string, DaySchedule>>(
    initialHours || DEFAULT_HOURS
  );
  const [isSaving, setIsSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const updateDay = (day: string, field: keyof DaySchedule, value: any) => {
    setHours(prev => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_URL}/businesses/${businessId}`, 
        { openingHours: hours },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Horarios guardados');
      setEditMode(false);
    } catch { toast.error('Error al guardar'); }
    finally { setIsSaving(false); }
  };

  const copyToAll = (sourceDay: string) => {
    const source = hours[sourceDay];
    const updated = { ...hours };
    DAYS.forEach(d => { updated[d] = { ...source }; });
    setHours(updated);
    toast.success('Horario copiado a todos los días');
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-secondary-900">🕐 Horarios de apertura</h3>
        <button
          onClick={() => editMode ? handleSave() : setEditMode(true)}
          disabled={isSaving}
          className="btn-primary text-sm"
        >
          {isSaving ? 'Guardando...' : editMode ? 'Guardar horarios' : '✏️ Editar horarios'}
        </button>
      </div>

      <div className="space-y-2">
        {DAYS.map(day => {
          const d = hours[day] || DEFAULT_HOURS[day];
          return (
            <div key={day} className={`rounded-12 ${!d.isOpen ? 'opacity-60' : ''}`}
              style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px' }}>
              <div className="flex items-center gap-3 flex-wrap">
                {editMode && (
                  <input
                    type="checkbox"
                    checked={d.isOpen}
                    onChange={e => updateDay(day, 'isOpen', e.target.checked)}
                    className="h-4 w-4 rounded"
                  />
                )}
                <span className="font-semibold text-secondary-900 w-24 text-sm">{day}</span>

                {d.isOpen ? (
                  editMode ? (
                    <div className="flex items-center gap-2 flex-wrap flex-1">
                      <input type="time" value={d.openTime} onChange={e => updateDay(day, 'openTime', e.target.value)} className="input w-28 text-sm" style={{fontVariantNumeric:"tabular-nums"}} />
                      <span className="text-secondary-400">—</span>
                      <input type="time" value={d.closeTime} onChange={e => updateDay(day, 'closeTime', e.target.value)} className="input w-28 text-sm" style={{fontVariantNumeric:"tabular-nums"}} />
                      <span className="text-xs text-secondary-400">{calcHours(d.openTime, d.closeTime, d.hasBreak ? d.breakStart : undefined, d.hasBreak ? d.breakEnd : undefined)}</span>
                      
                      <div className="flex items-center gap-2 ml-2">
                        <input type="checkbox" checked={d.hasBreak} onChange={e => updateDay(day, 'hasBreak', e.target.checked)} className="h-3 w-3" />
                        <span className="text-xs text-secondary-500">Descanso</span>
                      </div>

                      {d.hasBreak && (
                        <div className="flex items-center gap-2">
                          <input type="time" value={d.breakStart || '14:00'} onChange={e => updateDay(day, 'breakStart', e.target.value)} className="input w-24 text-sm" />
                          <span className="text-secondary-400 text-xs">—</span>
                          <input type="time" value={d.breakEnd || '16:00'} onChange={e => updateDay(day, 'breakEnd', e.target.value)} className="input w-24 text-sm" />
                        </div>
                      )}

                      <button onClick={() => copyToAll(day)} className="text-xs text-primary-600 hover:underline ml-auto">
                        Copiar a todos
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-sm text-secondary-600">
                      <span className="font-medium" style={{fontFamily:'monospace'}}>{d.openTime} – {d.closeTime}</span>
                      {d.hasBreak && d.breakStart && d.breakEnd && (
                        <span className="text-xs text-secondary-400">(Descanso {d.breakStart}–{d.breakEnd})</span>
                      )}
                      <span className="text-xs text-secondary-400">
                        {calcHours(d.openTime, d.closeTime, d.hasBreak ? d.breakStart : undefined, d.hasBreak ? d.breakEnd : undefined)}
                      </span>
                      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-700">Abierto</span>
                    </div>
                  )
                ) : (
                  <span className="text-sm text-secondary-400 italic">Cerrado</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
